import React, { useState } from 'react';
import {
  GrowthIcon,
  ExpandCollapseIcon,
} from '@/components/DesignSystem/Icons';
import { CareerPlannerProps } from '../types';
import tagularEvent from '@/utils/tagular/tagularEvent';

// Utility function to convert numbers to words
const oneToThreeToWord = (num: number): string => {
  const words = ['One', 'Two', 'Three'];
  return words[num - 1] || num.toString();
};

const CareerPlanner = ({
  steps,
  currentRole,
  targetRole,
  correlationId,
}: CareerPlannerProps) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([0]));

  const toggleStep = (index: number) => {
    const newExpandedSteps = new Set(expandedSteps);

    if (newExpandedSteps.has(index)) {
      newExpandedSteps.delete(index);
    } else {
      // Fire tagular event for step expand/collapse
      tagularEvent('ElementClicked', {
        correlationId: correlationId,
        webElement: {
          elementType: 'button',
          location: 'dexp-career-pathways',
          name: 'dexp-expand-steps-cta',
          position: 'dexp-career-pathways',
          text: `step-${index + 1}`,
        },
      });

      newExpandedSteps.add(index);
    }
    setExpandedSteps(newExpandedSteps);
  };

  return (
    <div className='flex-1 bg-white rounded-xl flex flex-col md:space-y-6 not-prose'>
      {/* Header */}
      <div>
        <span className='text-base md:text-lg font-bold text-gray-900 pb-2'>
          Career Pathway
        </span>
      </div>
      <div className='flex flex-col gap-4'>
        <div className='flex items-center gap-3 text-sm md:text-base'>
          <span className='bg-white rounded-full border border-gray-300 px-3 py-1 font-medium text-black'>
            Current
          </span>
          <span className='font-semibold text-gray-900'>{currentRole}</span>
        </div>

        <div className='relative space-y-2 md:space-y-4'>
          {steps.map((step, index) => (
            <>
              {/* Step header */}
              <div
                className='flex gap-6 items-center cursor-pointer'
                onClick={() => {
                  toggleStep(index);
                }}
              >
                <GrowthIcon />
                <div className='space-x-2'>
                  <span className='text-base md:text-lg font-semibold text-gray-900'>
                    Step {oneToThreeToWord(index + 1)}
                  </span>
                  <span className='text-gray-600 text-sm md:text-base font-medium'>
                    {step.timeEstimate}
                  </span>
                </div>
                <div className='flex justify-end ml-auto'>
                  <ExpandCollapseIcon
                    className={`transform transition-transform duration-200 ${
                      expandedSteps.has(index) ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </div>

              {/* Step content */}
              <div
                key={index}
                className='border-l-4 rounded-t-sm rounded-b-sm border-gray-200 ml-2'
              >
                <div className='ml-9 '>
                  <span className='text-sm md:text-base font-semibold text-gray-900 mb-2'>
                    {step.title}
                  </span>
                  {expandedSteps.has(index) && (
                    <>
                      <p className='text-gray-600 mb-3 leading-relaxed text-sm md:text-base'>
                        {step.description}
                      </p>

                      <span className='text-sm md:text-base font-medium text-gray-700'>
                        Key skills to develop:
                      </span>

                      <div className='flex flex-wrap gap-2 text-sm mt-2'>
                        {step.skills?.map((skill, skillIndex) => (
                          <span
                            key={skillIndex}
                            className='bg-white border border-teal-200 text-teal-700 px-4 py-1 rounded-full font-medium'
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          ))}
        </div>
        <div className='flex items-center gap-3 text-sm md:text-base'>
          <span className='bg-white rounded-full border border-gray-300 px-3 py-1 font-medium text-black'>
            Target
          </span>
          <span className='font-semibold text-gray-900'>{targetRole}</span>
        </div>
      </div>
    </div>
  );
};

export default CareerPlanner;
